import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuthStore } from '../stores/auth';
import { Home, Users, TrendingUp, Settings, Wand2, Sparkles } from 'lucide-react';
import { StatsTab } from './admin/StatsTab';
import { UsersTab } from './admin/UsersTab';
import { LLMConfigTab } from './admin/LLMConfigTab';
import { DirectorTab } from './admin/DirectorTab';
import { PromptCraftTab } from './admin/PromptCraftTab';

type TabType = 'overview' | 'users' | 'llm-config' | 'director' | 'promptcraft';

export const AdminDashboard: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabType>('overview');
  const navigate = useNavigate();
  const { user } = useAuthStore();

  // 检查管理员权限
  if (user?.role !== 'admin') {
    navigate('/dashboard');
    return null;
  }

  const tabs = [
    { id: 'overview' as TabType, label: '系统概览', icon: TrendingUp },
    { id: 'users' as TabType, label: '用户管理', icon: Users },
    { id: 'llm-config' as TabType, label: '大模型设置', icon: Settings },
    { id: 'director' as TabType, label: 'Director 工作流', icon: Wand2 },
    { id: 'promptcraft' as TabType, label: 'Prompt Craft', icon: Sparkles },
  ];

  return (
    <div className="min-h-screen bg-[#0d1117]">
      {/* 顶部导航栏 */}
      <div className="bg-[#161b22] border-b border-cyan-900/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <h1 className="text-2xl font-bold text-white">管理员控制台</h1>
            </div>
            <Link
              to="/"
              className="flex items-center space-x-2 px-4 py-2 text-gray-400 hover:text-white"
            >
              <Home className="h-5 w-5" />
              <span>返回首页</span>
            </Link>
          </div>
        </div>
      </div>

      {/* 标签导航 */}
      <div className="bg-[#161b22] border-b border-cyan-900/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="-mb-px flex space-x-8">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`group inline-flex items-center py-4 px-1 border-b-2 font-medium text-sm ${
                    isActive
                      ? 'border-cyan-500 text-cyan-400'
                      : 'border-transparent text-gray-400 hover:text-white hover:border-cyan-900/50'
                  }`}
                >
                  <Icon
                    className={`-ml-0.5 mr-2 h-5 w-5 ${
                      isActive ? 'text-cyan-400' : 'text-gray-500 group-hover:text-white'
                    }`}
                  />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>
      </div>

      {/* 主内容区域 */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {activeTab === 'overview' && <StatsTab />}
        {activeTab === 'users' && <UsersTab />}
        {activeTab === 'llm-config' && <LLMConfigTab />}
        {activeTab === 'director' && <DirectorTab />}
        {activeTab === 'promptcraft' && <PromptCraftTab />}
      </div>
    </div>
  );
};
